import { useState, useCallback, useEffect, useContext } from "react";
import {
  Contact,
  ContactService,
  PaginatedContactResponse,
} from "./contact.service";
import { AuthContext } from "../../../context/authcontext";

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasMore: boolean;
  pageSize: number;
}

interface UseContactsReturn {
  contacts: Contact[];
  loading: boolean;
  backgroundLoading: boolean;
  error: string | null;
  pagination: PaginationData;
  fetchContacts: (page?: number) => Promise<void>;
  refreshContacts: () => Promise<void>;
  addContact: (contact: Contact) => void;
  updateContact: (contact: Contact) => void;
  removeContact: (contactId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  setPageSize: (pageSize: number) => void;
  clearBackgroundLoading: () => void;
}

export const useContacts = (): UseContactsReturn => {
  const { selectedTenantId } = useContext(AuthContext);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasMore: false,
    pageSize: 12,
  });

  const [fetchedPages, setFetchedPages] = useState<Set<number>>(new Set());
  const [pageData, setPageData] = useState<Map<number, Contact[]>>(new Map());

  const clearBackgroundLoading = useCallback(() => {
    setBackgroundLoading(false);
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedReset = () => {
      timeoutId = setTimeout(() => {
        if (selectedTenantId) {
          setFetchedPages(new Set());
          setPageData(new Map());
          setContacts([]);
          setIsInitialized(false);
          setError(null);
          setPagination((prev) => ({
            ...prev,
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            hasMore: false,
          }));
        }
      }, 300);
    };

    debouncedReset();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedTenantId]);

  const fetchContacts = useCallback(async (page: number = 1, forceRefresh: boolean = false) => {
    if (!selectedTenantId) {
      setError('No tenant selected. Please select a tenant to view contacts.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response: PaginatedContactResponse = await ContactService.getContactsByPage(page, 12);
      
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: response.totalPages,
        totalCount: response.totalCount,
        hasMore: response.hasMore,
        pageSize: 12
      }));

      setPageData(prev => new Map(prev).set(page, response.contacts));
      setFetchedPages(prev => new Set(prev).add(page));

      setContacts(response.contacts);

      setLastFetched(new Date());
      setIsInitialized(true);
      
      setBackgroundLoading(true);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to fetch contacts. Please check your tenant selection.');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  const refreshContacts = useCallback(async () => {
    setFetchedPages(new Set());
    setPageData(new Map());
    setIsInitialized(false);
    await fetchContacts(1, true);
  }, [fetchContacts]);

  const addContact = useCallback((contact: Contact) => {
    setContacts((prev) => [contact, ...prev]);

    setPagination((prev) => ({
      ...prev,
      totalCount: prev.totalCount + 1,
    }));

    setFetchedPages(new Set());
    setPageData(new Map());
  }, []);

  const updateContact = useCallback((updatedContact: Contact) => {
    setContacts((prev) =>
      prev.map((contact) =>
        contact.contact_id === updatedContact.contact_id
          ? updatedContact
          : contact
      )
    );

    setPageData((prev) => {
      const newPageData = new Map(prev);
      newPageData.forEach((contacts, pageNum) => {
        const updatedContacts = contacts.map((contact) =>
          contact.contact_id === updatedContact.contact_id
            ? updatedContact
            : contact
        );
        newPageData.set(pageNum, updatedContacts);
      });
      return newPageData;
    });
  }, []);

  const removeContact = useCallback((contactId: number) => {
    setContacts((prev) =>
      prev.filter((contact) => contact.contact_id !== contactId)
    );

    setPagination((prev) => ({
      ...prev,
      totalCount: Math.max(0, prev.totalCount - 1),
    }));

    setFetchedPages(new Set());
    setPageData(new Map());
  }, []);

  const setPageSize = useCallback((newPageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
    }));
  }, []);

  return {
    contacts,
    loading,
    backgroundLoading,
    error,
    pagination,
    fetchContacts,
    refreshContacts,
    addContact,
    updateContact,
    removeContact,
    lastFetched,
    isInitialized,
    setPageSize,
    clearBackgroundLoading,
  };
};
